import { useState } from 'react';
import { ArrowLeft, ArrowRight, Clock, KeyRound, Lock, Mail, ShieldCheck } from 'lucide-react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { ROUTES } from '@/lib/constants/routes';
import { UI_STRINGS } from '@/lib/constants/ui-strings';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { CountdownTimer } from '@/components/ui/CountdownTimer';
import { OtpInput } from '@/components/ui/OtpInput';
import { AuthHero } from '@/components/common/AuthHero';
import { SectionCard } from '@/components/common/SectionCard';
import { ShieldNote } from '@/components/common/ShieldNote';
import { useVerifyInviteOtp } from '@/hooks/useVerifyInviteOtp';
import { useResendInviteOtp } from '@/hooks/useResendInviteOtp';
import { useCountdown } from '@/hooks/useCountdown';
import { OTP_UI } from '@/lib/constants/otp';
import { API_ERROR_CODES } from '@/lib/constants/error-codes';
import { getApiErrorCode, mapApiError } from '@/lib/utils/error.utils';

interface InviteVerifyLocationState {
  email?: string;
  /** False when the invite-accept OTP email failed to send (steer user to Resend). */
  otpEmailSent?: boolean;
}

const VERIFY_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.OTP_INVALID]: UI_STRINGS.INVITE_VERIFY.ERROR_INVALID,
  [API_ERROR_CODES.OTP_NOT_FOUND]: UI_STRINGS.INVITE_VERIFY.ERROR_INVALID,
  [API_ERROR_CODES.OTP_EXPIRED]: UI_STRINGS.INVITE_VERIFY.ERROR_EXPIRED,
  [API_ERROR_CODES.OTP_LOCKED]: UI_STRINGS.INVITE_VERIFY.ERROR_LOCKED,
};

const RESEND_ERROR_BY_CODE: Record<string, string> = {
  [API_ERROR_CODES.RATE_LIMITED]: UI_STRINGS.INVITE_VERIFY.ERROR_RATE_LIMITED,
};

/** Codes after which waiting out the cooldown makes no sense - resend now. */
const RESEND_UNLOCK_CODES = new Set<string>([
  API_ERROR_CODES.OTP_EXPIRED,
  API_ERROR_CODES.OTP_LOCKED,
]);

/**
 * Step 2 of invite acceptance (US-038/US-098): verify the emailed OTP code.
 * Mirrors `VerifyEmailPage` - same components, same resend/cooldown
 * mechanics - but scoped to the invite token and routed back into the invite
 * flow (not the general signup flow) on both the guard and the Back link.
 */
export function InviteVerifyOtpPage() {
  const { token = '' } = useParams<{ token: string }>();
  const location = useLocation();
  const email = (location.state as InviteVerifyLocationState | null)?.email;
  const inviteSignupPath = ROUTES.AUTH.INVITE_SIGNUP.replace(':token', token);

  const verify = useVerifyInviteOtp(token);
  const resend = useResendInviteOtp(token);

  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string>();
  // Remounts the expiry countdown when a new code is sent.
  const [timerKey, setTimerKey] = useState(0);
  // Resend unlocks after a short cooldown (not the full 10-minute code TTL),
  // or immediately when the server says the code is expired/locked.
  const cooldown = useCountdown(OTP_UI.RESEND_COOLDOWN_SECONDS);
  const verifyErrorCode = getApiErrorCode(verify.error);
  // Step 1 completed but the OTP email failed to send: warn and unlock Resend.
  const sendFailed =
    (location.state as InviteVerifyLocationState | null)?.otpEmailSent === false &&
    !resend.isSuccess;
  const resendReady =
    cooldown.expired ||
    sendFailed ||
    (verifyErrorCode !== undefined && RESEND_UNLOCK_CODES.has(verifyErrorCode));

  // Arriving without an email means step 1 was skipped - send the user back to
  // the invite form. Note this does NOT fire on refresh: React Router
  // serialises `location.state` into `history.state`, which the browser
  // restores, so a refresh here keeps the email and stays on this screen. That
  // is fine for a non-secret address; it is exactly why the login 2FA step
  // holds its pre-auth credential in memory instead (see lib/auth/pre-auth.ts).
  if (!email) {
    return <Navigate to={inviteSignupPath} replace />;
  }

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!new RegExp(`^\\d{${OTP_UI.CODE_LENGTH}}$`).test(code)) {
      setCodeError(`Enter the ${OTP_UI.CODE_LENGTH}-digit code`);
      return;
    }
    setCodeError(undefined);
    verify.mutate({ code });
  };

  const handleResend = () => {
    // Cooldown/timer only restart once the resend actually succeeded - a
    // failed resend must not silently re-lock the button for 60s.
    resend.mutate(undefined, {
      onSuccess: () => {
        cooldown.reset();
        setTimerKey((key) => key + 1);
      },
    });
  };

  return (
    <div className="flex flex-col items-center bg-page-bg px-4 py-16">
      <AuthHero
        badgeIcon={<ShieldCheck size={14} />}
        badgeText={UI_STRINGS.INVITE_VERIFY.BADGE}
        heading={
          <>
            {UI_STRINGS.INVITE_VERIFY.HEADING_PREFIX}
            <span className="text-green">{UI_STRINGS.INVITE_VERIFY.HEADING_HIGHLIGHT}</span>
          </>
        }
        subtitleClassName="max-w-2xl"
        subtitle={
          <>
            <p>
              {UI_STRINGS.INVITE_VERIFY.SUBTITLE_PREFIX}{' '}
              <span className="whitespace-nowrap font-semibold text-navy">{email}</span>
            </p>
            <p className="mt-1 text-sm">{UI_STRINGS.INVITE_VERIFY.SUBTITLE_LINE2}</p>
          </>
        }
      />

      <div className="mt-10 flex w-full max-w-3xl flex-col gap-6">
        {/* "Code sent to" banner */}
        <div className="flex items-center justify-between gap-4 rounded-card border border-green/30 bg-green/10 px-6 py-4">
          <div className="flex items-center gap-4">
            <span
              aria-hidden="true"
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-green/20 text-dkgreen"
            >
              <Mail size={18} />
            </span>
            <div>
              <p className="font-sans text-[11px] font-semibold uppercase tracking-wide text-dkgreen">
                {UI_STRINGS.INVITE_VERIFY.SENT_TO_LABEL}
              </p>
              <p className="font-sans text-sm font-semibold text-navy">{email}</p>
              <p className="text-xs text-subtext">{UI_STRINGS.INVITE_VERIFY.SENT_TO_HINT}</p>
            </div>
          </div>
          {!sendFailed && (
            <Badge variant="green-outline">&bull; {UI_STRINGS.INVITE_VERIFY.SENT_CHIP}</Badge>
          )}
        </div>

        {sendFailed && (
          <p
            role="alert"
            className="rounded-card border border-warning/30 bg-warning/10 px-6 py-3 text-sm text-warning"
          >
            {UI_STRINGS.INVITE_VERIFY.SEND_FAILED}
          </p>
        )}

        <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-6">
          <SectionCard
            title={UI_STRINGS.INVITE_VERIFY.CARD_TITLE}
            subtitle={UI_STRINGS.INVITE_VERIFY.CARD_SUBTITLE}
            icon={<KeyRound size={20} />}
            className="pb-0"
          >
            {verify.isError && (
              <p role="alert" className="mb-4 text-center text-sm text-danger">
                {mapApiError(verify.error, VERIFY_ERROR_BY_CODE, UI_STRINGS.INVITE_VERIFY.ERROR)}
              </p>
            )}
            {resend.isSuccess && (
              <p role="status" className="mb-4 text-center text-sm text-dkgreen">
                {UI_STRINGS.INVITE_VERIFY.RESENT}
              </p>
            )}
            {resend.isError && (
              <p role="alert" className="mb-4 text-center text-sm text-danger">
                {mapApiError(
                  resend.error,
                  RESEND_ERROR_BY_CODE,
                  UI_STRINGS.INVITE_VERIFY.RESEND_ERROR,
                )}
              </p>
            )}

            <div className="flex flex-col items-center gap-4 py-4">
              <OtpInput
                label={UI_STRINGS.INVITE_VERIFY.CODE_LABEL}
                value={code}
                onChange={setCode}
                length={OTP_UI.CODE_LENGTH}
                error={codeError}
              />
              <p className="flex items-center gap-1.5 text-xs text-slate">
                <Lock size={12} aria-hidden="true" />
                <span>
                  {UI_STRINGS.INVITE_VERIFY.EXPIRES_NOTE_PREFIX}
                  <span className="font-semibold text-navy">
                    {UI_STRINGS.INVITE_VERIFY.EXPIRES_NOTE_BOLD}
                  </span>
                  {UI_STRINGS.INVITE_VERIFY.EXPIRES_NOTE_SUFFIX}
                </span>
              </p>
            </div>

            <div className="-mx-8 mt-4 flex items-center justify-between gap-4 rounded-b-card border-t border-divider bg-page-bg px-8 py-4">
              <div className="flex items-center gap-3">
                <span
                  aria-hidden="true"
                  className="flex h-9 w-9 items-center justify-center rounded-full border-2 border-dkgreen text-dkgreen"
                >
                  <Clock size={16} />
                </span>
                <div className="leading-tight">
                  <p className="text-xs text-slate">{UI_STRINGS.INVITE_VERIFY.EXPIRES_IN}</p>
                  <CountdownTimer key={timerKey} seconds={OTP_UI.CODE_TTL_SECONDS} />
                </div>
              </div>
              <p className="text-sm text-slate">
                {UI_STRINGS.INVITE_VERIFY.NO_CODE}{' '}
                <Button
                  type="button"
                  variant="link"
                  disabled={!resendReady || resend.isPending}
                  onClick={handleResend}
                  className="align-baseline disabled:text-slate"
                >
                  {resend.isPending
                    ? UI_STRINGS.INVITE_VERIFY.RESENDING
                    : UI_STRINGS.INVITE_VERIFY.RESEND}
                </Button>
              </p>
            </div>
          </SectionCard>

          <SectionCard>
            <ShieldNote className="justify-end">
              {UI_STRINGS.INVITE_VERIFY.ENCRYPTION_NOTE}
            </ShieldNote>
            <div className="mt-4 flex items-center justify-end gap-6">
              <Link
                to={inviteSignupPath}
                className="inline-flex items-center gap-1.5 font-sans text-sm font-medium text-navy hover:text-dkgreen"
              >
                <ArrowLeft size={16} aria-hidden="true" />
                {UI_STRINGS.INVITE_VERIFY.BACK}
              </Link>
              <Button
                type="submit"
                variant="navy"
                disabled={verify.isPending}
                className="inline-flex items-center gap-2 px-8 py-3.5 text-base drop-shadow-md"
              >
                {verify.isPending
                  ? UI_STRINGS.INVITE_VERIFY.SUBMITTING
                  : UI_STRINGS.INVITE_VERIFY.SUBMIT}
                <ArrowRight size={18} aria-hidden="true" />
              </Button>
            </div>
          </SectionCard>
        </form>
      </div>
    </div>
  );
}
